import React, { useEffect, useRef, useState } from 'react';
import { DiceValue, SpecialDiceType } from '../types';
import { Shield, Bomb, IterationCcw, Sparkles } from 'lucide-react';

interface DieProps {
  value: DiceValue | SpecialDiceType | null;
  size?: 'sm' | 'md' | 'lg';
  isRolling?: boolean;
  glow?: boolean;
  className?: string;
}

const pipPositions: Record<number, number[][]> = {
  1: [[50, 50]],
  2: [[25, 25], [75, 75]],
  3: [[25, 25], [50, 50], [75, 75]],
  4: [[25, 25], [25, 75], [75, 25], [75, 75]],
  5: [[25, 25], [25, 75], [50, 50], [75, 25], [75, 75]],
  6: [[25, 25], [25, 50], [25, 75], [75, 25], [75, 50], [75, 75]],
};

const Pips: React.FC<{ value: number }> = ({ value }) => (
  <>
    {pipPositions[value].map(([x, y], i) => (
      <div
        key={i}
        className={`absolute rounded-full ${value === 1 ? 'bg-red-600' : 'bg-black'}`}
        style={{
          width: '18%',
          height: '18%',
          left: `${x}%`,
          top: `${y}%`,
          transform: 'translate(-50%, -50%)'
        }}
      />
    ))}
  </>
);

const Die: React.FC<DieProps> = ({ value, size = 'md', isRolling, glow = false, className = "" }) => {
  const sizeClasses = {
    sm: 'w-7 h-7 p-1',
    md: 'w-12 h-12 lg:w-20 lg:h-20 p-1.5 lg:p-3',
    lg: 'w-16 h-16 lg:w-28 lg:h-28 p-3 lg:p-6'
  };

  // While rolling, cycle random faces for a tumbling feel
  const [rollFace, setRollFace] = useState<number>(() => 1 + Math.floor(Math.random() * 6));
  useEffect(() => {
    if (!isRolling) return;
    const interval = setInterval(() => {
      setRollFace((prev) => {
        const next = 1 + Math.floor(Math.random() * 6);
        return next === prev ? (next % 6) + 1 : next;
      });
    }, 90);
    return () => clearInterval(interval);
  }, [isRolling]);

  // Squash & stretch settle when a roll resolves into a value
  const wasRollingRef = useRef(!!isRolling);
  const [settling, setSettling] = useState(false);
  useEffect(() => {
    const wasRolling = wasRollingRef.current;
    wasRollingRef.current = !!isRolling;
    if (wasRolling && !isRolling && value) {
      setSettling(true);
      const timer = setTimeout(() => setSettling(false), 360);
      return () => clearTimeout(timer);
    }
  }, [isRolling, value]);

  if (isRolling) {
    return (
      <div className={`${sizeClasses[size]} ${className} bg-white border-2 lg:border-4 border-black rounded-lg relative dice-tumble shadow-[2px_2px_0px_0px_rgba(0,0,0,0.5)] lg:shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)]`}>
        <Pips value={rollFace} />
      </div>
    );
  }

  if (!value) return <div className={`${sizeClasses[size]} border-2 lg:border-4 border-dashed border-gray-700 rounded-lg bg-black/20`} />;

  // Special Dice Logic
  if (typeof value === 'string') {
    let bgColor = 'bg-white';
    let icon = null;

    switch (value) {
      case 'joker':
        bgColor = 'bg-gradient-to-br from-yellow-400 via-pink-400 to-purple-500 border-yellow-200';
        icon = <Sparkles className="w-full h-full p-0.5 lg:p-2" color="white" fill="white" />;
        break;
      case 'shield':
        bgColor = 'bg-blue-500 border-blue-300';
        icon = <Shield className="w-full h-full p-0.5 lg:p-2" color="white" fill="white" />;
        break;
      case 'bomb':
        bgColor = 'bg-red-500 border-red-300';
        icon = <Bomb className="w-full h-full p-0.5 lg:p-2" color="white" />;
        break;
      case 'reroll':
        bgColor = 'bg-emerald-500 border-emerald-300';
        icon = <IterationCcw className="w-full h-full p-0.5 lg:p-1 text-white" strokeWidth={3} />;
        break;
    }

    return (
      <div className={`${sizeClasses[size]} ${className} ${bgColor} ${settling ? 'dice-settle' : ''} border-2 lg:border-4 rounded-lg flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(0,0,0,0.4)] lg:shadow-[4px_4px_0px_0px_rgba(0,0,0,0.4)] transition-transform duration-200`}>
        {icon}
      </div>
    );
  }

  return (
    <div className={`${sizeClasses[size]} ${className} ${settling ? 'dice-settle' : ''} ${glow ? 'dice-glow' : ''} bg-white border-2 lg:border-4 border-black rounded-lg relative shadow-[2px_2px_0px_0px_rgba(0,0,0,0.4)] lg:shadow-[4px_4px_0px_0px_rgba(0,0,0,0.4)] transition-transform duration-200`}>
      <Pips value={value} />
    </div>
  );
};

export default Die;
